import {addDays} from 'date-fns'

import {parseApiDate, toApiDateString} from '../jobs/job-schedule-format'
import {bookingCoversWholeJob, computeAllocationStatus} from './allocation'
import {allocationStatusForRow} from './attention-lens'
import {type BoardLaneSections, classifyBoardLane} from './board-lanes'
import {buildCommitmentJobDisplay} from './commitment-job-display'
import {calendarDaySpan, jobIntersectsHorizon} from './job-horizon'
import {buildUnifiedJobRows, type TimelineJobRow} from './job-rows'
import {deriveOperationalState} from './operational-state'
import {bookingCoversDay, type DispatchTimelineData, type ScheduleDate} from './types'

export type TerminalRowEntry = {
    job: TimelineJobRow
    viewDayOffset: number
    isContinuationDay: boolean
    lane: ReturnType<typeof classifyBoardLane>
    display: ReturnType<typeof buildCommitmentJobDisplay>
    operational: ReturnType<typeof deriveOperationalState>
}

export function jobsVisibleInHorizon(
    data: DispatchTimelineData,
    jobs: TimelineJobRow[] = buildUnifiedJobRows(data),
): TimelineJobRow[] {
    const dayCount = data.visibleDays?.length ?? 1
    const viewDayOriginOffset = data.viewDayOriginOffset ?? 0
    return jobs.filter((job) => jobRowIntersectsHorizon(job, dayCount, viewDayOriginOffset))
}

function jobRowIntersectsHorizon(
    job: TimelineJobRow,
    dayCount: number,
    viewDayOriginOffset: number,
): boolean {
    return jobIntersectsHorizon(job.span, job.scheduleKind, dayCount, viewDayOriginOffset)
}

export function jobOnDay(job: TimelineJobRow, day: string): TimelineJobRow {
    const schedule = job.unassignedJob?.schedule ?? job.schedule
    if (
        job.allocations.every(
            (allocation) =>
                allocation.kind !== 'worker' || bookingCoversWholeJob(allocation.days, schedule),
        )
    ) {
        return job
    }
    const allocations = job.allocations
        .filter(
            (allocation) => allocation.kind !== 'worker' || bookingCoversDay(allocation.days, day),
        )
        .map((allocation) =>
            allocation.kind === 'worker' && allocation.days
                ? {...allocation, days: undefined}
                : allocation,
        )
    const unassignedJob = job.unassignedJob
        ? {...job.unassignedJob, allocations, allocationStatus: undefined}
        : undefined
    return {
        ...job,
        allocations,
        unassignedJob,
        allocationStatus: unassignedJob
            ? computeAllocationStatus(unassignedJob)
            : allocations.length === job.allocations.length
              ? job.allocationStatus
              : 'partially_allocated',
    }
}

export function todayViewOffset(visibleDays: ScheduleDate[] | undefined): number | undefined {
    const index = (visibleDays ?? []).findIndex((day) => day.isToday)
    return index >= 0 ? index : undefined
}

export function expandJobsAcrossDays(
    jobs: TimelineJobRow[],
    visibleDayCount: number,
    viewDayOriginOffset = 0,
    todayOffset?: number,
): TerminalRowEntry[] {
    const expanded: TerminalRowEntry[] = []

    for (const job of jobs) {
        const start = job.span?.dayOffset ?? 0
        const span = calendarDaySpan(job.span, job.scheduleKind)
        const allocationStatus = allocationStatusForRow(job)
        const display = buildCommitmentJobDisplay(job)
        const operational = deriveOperationalState(job, allocationStatus)
        const lane = classifyBoardLane(job, operational)

        const visibleStart = viewDayOriginOffset
        const visibleEnd = viewDayOriginOffset + visibleDayCount - 1
        const jobEnd = job.span?.openEnded
            ? Math.max(start + span - 1, visibleEnd)
            : start + span - 1

        if (jobEnd < visibleStart) {
            if (
                !jobIntersectsHorizon(
                    job.span,
                    job.scheduleKind,
                    visibleDayCount,
                    viewDayOriginOffset,
                )
            ) {
                continue
            }

            expanded.push({
                job,
                viewDayOffset: 0,
                isContinuationDay: false,
                lane,
                display,
                operational,
            })
            continue
        }

        if (start > visibleEnd) {
            continue
        }

        const firstVisibleDay = Math.max(start, visibleStart)
        const lastVisibleDay = Math.min(jobEnd, visibleEnd)

        const startDate = job.schedule?.startDate
        for (let dayOffset = firstVisibleDay; dayOffset <= lastVisibleDay; dayOffset += 1) {
            const viewDayOffset = dayOffset - viewDayOriginOffset
            const dayJob =
                startDate != null
                    ? jobOnDay(
                          job,
                          toApiDateString(addDays(parseApiDate(startDate), dayOffset - start)),
                      )
                    : job

            if (dayJob === job) {
                expanded.push({
                    job,
                    viewDayOffset,
                    isContinuationDay: dayOffset > start,
                    lane,
                    display,
                    operational,
                })
                continue
            }

            const dayStatus = allocationStatusForRow(dayJob)
            const dayOperational = deriveOperationalState(dayJob, dayStatus)
            expanded.push({
                job: dayJob,
                viewDayOffset,
                isContinuationDay: dayOffset > start,
                lane: classifyBoardLane(dayJob, dayOperational),
                display: buildCommitmentJobDisplay(dayJob),
                operational: dayOperational,
            })
        }
    }

    return collapseMultiDayEntries(expanded, todayOffset)
}

const LANE_URGENCY: Record<TerminalRowEntry['lane'], number> = {
    dispatched: 0,
    notify_drivers: 1,
    needs_action: 2,
}

function continuationHasDistinctAction(
    startDay: TerminalRowEntry,
    continuation: TerminalRowEntry,
): boolean {
    if (continuation.lane !== startDay.lane) {
        return LANE_URGENCY[continuation.lane] > LANE_URGENCY[startDay.lane]
    }

    return (
        continuation.display.missingLabels.length > 0 && startDay.display.missingLabels.length === 0
    )
}

/** One canonical row per multi-day job unless a later day needs different action. */
export function collapseMultiDayEntries(
    entries: TerminalRowEntry[],
    todayOffset?: number,
): TerminalRowEntry[] {
    const byJobId = new Map<string, TerminalRowEntry[]>()

    for (const entry of entries) {
        const group = byJobId.get(entry.job.id) ?? []
        group.push(entry)
        byJobId.set(entry.job.id, group)
    }

    const collapsed: TerminalRowEntry[] = []

    for (const jobEntries of byJobId.values()) {
        if (jobEntries.length === 1) {
            collapsed.push(jobEntries[0]!)
            continue
        }

        const today =
            todayOffset === undefined
                ? undefined
                : jobEntries.find((entry) => entry.viewDayOffset === todayOffset)
        const canonical =
            today ?? jobEntries.find((entry) => !entry.isContinuationDay) ?? jobEntries[0]!
        const laterDay = jobEntries
            .filter(
                (entry) =>
                    entry.viewDayOffset > canonical.viewDayOffset &&
                    continuationHasDistinctAction(canonical, entry),
            )
            .reduce<
                TerminalRowEntry | undefined
            >((worst, entry) => (worst && LANE_URGENCY[worst.lane] >= LANE_URGENCY[entry.lane] ? worst : entry), undefined)

        if (today && laterDay) {
            collapsed.push(today, laterDay)
            continue
        }
        collapsed.push(laterDay ?? canonical)
    }

    return collapsed
}

function sectionsFromEntries(entries: TerminalRowEntry[]): BoardLaneSections {
    const sections: BoardLaneSections = {
        needs_action: [],
        notify_drivers: [],
        dispatched: [],
    }

    for (const entry of entries) {
        const boardLaneJob = {
            job: entry.job,
            display: entry.display,
            operational: entry.operational,
            lane: entry.lane,
        }
        sections[entry.lane].push(boardLaneJob)
    }

    return sections
}

export type DayGroupedTerminalSections = {
    day: ScheduleDate
    dayOffset: number
    entries: TerminalRowEntry[]
    sections: BoardLaneSections
}

export function groupTerminalSectionsByDay(
    data: DispatchTimelineData,
    jobs: TimelineJobRow[],
): DayGroupedTerminalSections[] {
    const visibleDays = data.visibleDays ?? [data.scheduleDate]
    const viewDayOriginOffset = data.viewDayOriginOffset ?? 0
    const expanded = expandJobsAcrossDays(
        jobs,
        visibleDays.length,
        viewDayOriginOffset,
        todayViewOffset(visibleDays),
    )
    const byDay = new Map<number, TerminalRowEntry[]>()

    for (let dayOffset = 0; dayOffset < visibleDays.length; dayOffset += 1) {
        byDay.set(dayOffset, [])
    }

    for (const entry of expanded) {
        byDay.get(entry.viewDayOffset)?.push(entry)
    }

    return visibleDays.map((day, dayOffset) => {
        const entries = byDay.get(dayOffset) ?? []
        return {
            day,
            dayOffset,
            entries,
            sections: sectionsFromEntries(entries),
        }
    })
}
